import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { APP_DATA_KEYS, QUESTION_PATHS } from '../../constants/app-data.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatAuditPlanGridFilterRequest } from '../../../app/interfaces/maat-audit-plan-grid-filter-request.interface';
import { ExportToDataTablesRequest } from '../../interfaces/export-to-data-tables-request.interface';
import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';

import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { NetworkConnectivityService } from '../../services/network-connectivity.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-maat-audit-plan-list',
  templateUrl: './maat-audit-plan-list.component.html',
  styleUrls: ['./maat-audit-plan-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatAuditPlanListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private filterHandler: GridDataFilterHandler<MaatAuditPlanGridFilterRequest>;

  private gridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private excelExportService: ExcelExportService,
    private networkConnectivityService: NetworkConnectivityService,
    private formConfigurationParserService: FormConfigurationParserService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatAuditPlanList,
      () => API_ENDPOINTS.dashboard.getMaatAuditPlans,
      true);

    this.filterHandler = new GridDataFilterHandler<MaatAuditPlanGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatAuditPlans,
        searchQuestionPath: [...QUESTION_PATHS.maatAuditPlanList.search],
        filterQuestionPath: [...QUESTION_PATHS.maatAuditPlanList.filter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          planStatuses: filterQuestionModel?.planStatuses,
          oohOffice: filterQuestionModel?.oohOffice,
          planType: filterQuestionModel?.planType,
          complianceOfficer: filterQuestionModel?.complianceOfficer
        }),
        getDefaultFilterRequest: () => ({
          complianceOfficer: this.appDataService.getAppData<string>(APP_DATA_KEYS.hiipComplianceOfficerId)
        })
      });

    this.gridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatAuditPlanList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatAuditPlans);
  }

  ngOnInit(): void {
    this.ignoreCanDeactivateGuard = true;
    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.filterHandler.setInitialFilterRequest();
    });
  }

  ngOnDestroy(): void {
    this.gridDataClientHandler.unsubscribe();
    this.completeDestroy();
  }

  formLoad(event: any): void {
    this.configData = event.configData;
    this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.gridDataClientHandler.subscribe())
      .then(() => this.loadMaatAuditPlanGridDataOnFormLoad(this.filterHandler.getInitialFilterRequest()));
  }

  filterData(event: any): void {
    this.loadMaatAuditPlanGridData(this.filterHandler.saveFilterRequest(event.action));
  }

  actionButton_maatAuditPlanPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatAuditPlanId = event.event.data.id;
    this.router.navigate(['/', ROUTE_CONSTANTS.maatJob.listPrefix, maatAuditPlanId]);
  }

  actionButton_maatAuditPlanPlaceholder_datagrid_custom_bulkExport_Clicked(event: any): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }
    if (this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags).isMaatHeadContractor) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }
    const maatAuditPlanExportToDataTablesRequest = this.getMaatAuditPlanExportToDataTablesRequest();
    this.excelExportService.exportMaatAuditPlansToXlsx(maatAuditPlanExportToDataTablesRequest);
  }

  private loadMaatAuditPlanGridDataOnFormLoad(filterRequest: MaatAuditPlanGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridDataOnFormLoad(filterRequest, onGridLoaded);
  }

  private loadMaatAuditPlanGridData(filterRequest: MaatAuditPlanGridFilterRequest): void {
    const onGridLoaded = () => this.gridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(filterRequest, onGridLoaded);
  }

  private getMaatAuditPlanExportToDataTablesRequest(): ExportToDataTablesRequest {
    const entities: { id: string; [key: string]: any; }[] = this.getQuestionValue(this.model, ...QUESTION_PATHS.maatAuditPlanList.placeholder);
    const entityIds = entities.map(entity => entity.id);
    const organisationExportToDataTablesRequest: ExportToDataTablesRequest = { entityIds };
    return organisationExportToDataTablesRequest;
  }
}
